import { RefObject, useLayoutEffect, useState } from "react"

export type Position = {
  top: number
  left: number
}

export type UseElementAdjustedPositionProps = {
  ref: RefObject<HTMLElement>
  x: number
  y: number
  padding?: number
}

/**
 * Custom hook that adjusts element position to prevent it from being cut off by viewport edges.
 *
 * @param props - Configuration object
 * @param props.ref - Reference to the HTML element to position
 * @param props.x - Initial x coordinate
 * @param props.y - Initial y coordinate
 * @param props.padding - Optional padding from viewport edges (default: 8) - pixels
 * @returns Adjusted position object with top and left coordinates
 */
export function useElementAdjustedPosition({
  ref,
  x,
  y,
  padding = 8,
}: UseElementAdjustedPositionProps): Position {
  const [position, setPosition] = useState<Position>({ top: y, left: x })

  useLayoutEffect(() => {
    if (ref.current) {
      const elementRect = ref.current.getBoundingClientRect()
      const viewportWidth = window.innerWidth
      const viewportHeight = window.innerHeight

      let adjustedX = x
      let adjustedY = y

      // check if element goes beyond right edge
      if (x + elementRect.width > viewportWidth) {
        adjustedX = viewportWidth - elementRect.width - padding
      }

      // check if element goes beyond bottom edge
      if (y + elementRect.height > viewportHeight) {
        adjustedY = viewportHeight - elementRect.height - padding
      }

      // ensure element doesn't go beyond left/top edges
      adjustedX = Math.max(padding, adjustedX)
      adjustedY = Math.max(padding, adjustedY)

      setPosition({ top: adjustedY, left: adjustedX })
    }
  }, [ref, x, y, padding])

  return position
}
